import * as React from 'react';

import { OFFLINE_NOTICE_BODY, OFFLINE_NOTICE_TITLE, useConnectivity } from './connectivity';
import { InAppNotice } from './in-app-notice';

/**
 * The honest connectivity state (`MEM-043` AC-3).
 *
 * ## What it is for
 *
 * The shell is cached, so the app opens at a venue with no signal instead of showing a browser
 * error page (AC-2). Everything *inside* the shell then renders from whatever react-query has in
 * memory — which on a fresh open is nothing, and on a resumed one is whatever was last fetched.
 * The second case is the danger `MEM-043`'s Notes name: a match list from Tuesday, drawn on
 * Thursday with no qualification, is worse than an empty state because the member acts on it.
 *
 * This notice is the qualification. It sits above the content, it says the app cannot reach
 * H2HSpark, and it says what is on screen may be out of date.
 *
 * ## What it deliberately is not
 *
 * **It is not a connected indicator.** There is no green counterpart, because `navigator.onLine`
 * being true proves nothing — see `connectivity.ts`. The notice appears when the browser is
 * certain there is no network and is absent the rest of the time, including when the network is
 * up and the API is down. A member finds that one out by trying, and the screen they tried on
 * tells them (`MEM-031` AC-3).
 *
 * **It is not a queue, and it does not offer to become one.** Its copy says check-ins and
 * connections are not saved while offline, which is the product's actual behaviour
 * (`MEM-043` AC-4) said out loud rather than left for a member to discover after the door closed.
 *
 * **It is not dismissible.** A member who dismissed it would be back to an unqualified stale
 * screen, which is the state the notice exists to prevent. It goes away when connectivity does.
 */
export function OfflineNotice(): React.ReactElement | null {
  const { offline } = useConnectivity();
  if (!offline) return null;

  return (
    <InAppNotice
      variant="warning"
      title={OFFLINE_NOTICE_TITLE}
      body={OFFLINE_NOTICE_BODY}
      className="mb-4"
    />
  );
}
